import React from 'react';
import { flagCellClass, type FlagLevel } from '../utils/lineItemFlags';

/**
 * The key to a grid's highlighted cells.
 *
 * ── WHY IT EXISTS ────────────────────────────────────────────────────────────
 * Two grids highlight cells and they did not agree. `VendorInvoicesPage` asks
 * `lineItemFlags.flagCellClass` — pink / amber / blue — and explains the three
 * in a legend. `QuotationDetailPage` hard-coded its own Tailwind strings inline
 * at six sites and explained none of them, so it had SIX highlight states drawn
 * in FIVE colours, two of which existed nowhere else in the app:
 *
 *     sky      not in Master Catalogue     (its own colour)
 *     sky      differs from the RFQ ask     (same colour, different meaning)
 *     amber    vessel asked for 0
 *     amber    quoted in a different unit
 *     amber    markup out of date
 *     indigo   restated from the RFQ ask    (its own colour)
 *
 * The clash that mattered: sky-50 next to blue-50 reads as one colour, and on
 * the invoice grid blue means "a person confirmed this" — settled, nothing to
 * do — while on the quotation grid it meant "not in the catalogue", which is
 * the opposite. Somebody who learned one screen read the other backwards.
 *
 * ── THE VOCABULARY ───────────────────────────────────────────────────────────
 * One meaning per colour, everywhere, and it is the invoice grid's because that
 * is the one that was already written down:
 *
 *     pink   (error)             missing or wrong — fix it
 *     amber  (warn)              we guessed it — check it
 *     blue   (manual/confirmed)  a person typed or confirmed it
 *
 * ── WHY THE SWATCH IS NOT A LITERAL ──────────────────────────────────────────
 * Each swatch is painted by `flagCellClass` — the same call the CELL makes. A
 * legend that restates its colours as its own Tailwind strings is a second copy
 * that goes stale the first time a shade is tuned, and a legend that quietly
 * disagrees with the grid is worse than none: it is confidently wrong. Passing
 * the level through the real function means the swatch cannot drift from the
 * cell it describes, because there is nothing to keep in step.
 *
 * The MEANING is per-page and comes from the caller. "Pink" is "missing or
 * wrong" on both grids, but what to do about it differs — on an invoice some
 * pink cells block "Confirm & sync"; on a quotation a pink cell is an item to
 * add to the Master Catalogue. The colour is universal, the instruction is not.
 */

/** What ops calls each level. The colour's name, not the flag's name. */
export const HIGHLIGHT_COLOUR_NAME: Record<FlagLevel, string> = {
  error: 'Pink',
  warn: 'Yellow',
  manual: 'Blue',
  confirmed: 'Blue',
};

/**
 * One colour chip, painted by the cell's own function.
 *
 * Exported because swatches appear outside the legend too — the invoice grid
 * puts them beside its "3 missing or unreadable" counts, which is where the
 * question is actually asked. Those were hand-written copies of the same
 * Tailwind strings.
 */
export function HighlightSwatch({
  level,
  className = 'h-3 w-3',
}: {
  level: FlagLevel;
  className?: string;
}) {
  return (
    <span
      className={`rounded-sm shrink-0 ${className} ${flagCellClass({ level, reason: '' })}`}
      aria-hidden="true"
    />
  );
}

export type HighlightLegendItem = {
  level: FlagLevel;
  /** What this colour means ON THIS SCREEN, and what to do about it. */
  meaning: React.ReactNode;
};

/**
 * @param items  one row per colour the grid actually uses — a legend listing a
 *               colour the screen never draws teaches a rule that is not there
 * @param footer trailing note, e.g. "Hover any highlighted cell to see why"
 */
export function CellHighlightLegend({
  items,
  footer,
}: {
  items: HighlightLegendItem[];
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <HighlightSwatch level={item.level} className="mt-0.5 h-3 w-3" />
          <span>
            <span className="font-semibold">{HIGHLIGHT_COLOUR_NAME[item.level]}</span> — {item.meaning}
          </span>
        </div>
      ))}
      {footer ? <p className="text-slate-500">{footer}</p> : null}
    </div>
  );
}
